import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import en from './translations/en';
import es from './translations/es';

const STORAGE_KEY = 'superadmin_locale';
const DEFAULT_LOCALE = 'en';
const LOCALES = { en, es };

function getNested(obj, path) {
  const keys = path.split('.');
  let v = obj;
  for (const k of keys) {
    v = v?.[k];
    if (v === undefined) return undefined;
  }
  return v;
}

function interpolate(str, params = {}) {
  if (typeof str !== 'string') return str;
  return Object.keys(params).reduce(
    (s, key) => s.replace(new RegExp(`\\{${key}\\}`, 'g'), String(params[key])),
    str
  );
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_LOCALE;
    } catch {
      return DEFAULT_LOCALE;
    }
  });

  const setLocale = useCallback((newLocale) => {
    const next = newLocale === 'es' || newLocale === 'en' ? newLocale : DEFAULT_LOCALE;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {}
    return next;
  }, []);

  const t = useCallback((key, params) => {
    const dict = LOCALES[locale] || LOCALES[DEFAULT_LOCALE];
    const value = getNested(dict, key);
    if (value === undefined) return key;
    return interpolate(value, params);
  }, [locale]);

  const localeInfo = useMemo(() => {
    const isEn = locale === 'en';
    return {
      locale,
      dateLocale: isEn ? 'en-US' : 'es-ES',
      currencyCode: isEn ? 'USD' : 'EUR'
    };
  }, [locale]);

  const formatDate = useCallback((date, options = {}) => {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat(localeInfo.dateLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    }).format(d);
  }, [localeInfo.dateLocale]);

  const formatDateTime = useCallback((date, options = {}) => {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat(localeInfo.dateLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...options
    }).format(d);
  }, [localeInfo.dateLocale]);

  const formatCurrency = useCallback((amount, currencyCode = null) => {
    const code = currencyCode || localeInfo.currencyCode;
    return new Intl.NumberFormat(localeInfo.dateLocale, {
      style: 'currency',
      currency: code
    }).format(amount);
  }, [localeInfo.dateLocale, localeInfo.currencyCode]);

  const formatNumber = useCallback((num, options = {}) => {
    return new Intl.NumberFormat(localeInfo.dateLocale, options).format(num);
  }, [localeInfo.dateLocale]);

  const value = useMemo(() => ({
    locale,
    setLocale,
    t,
    formatDate,
    formatDateTime,
    formatCurrency,
    formatNumber,
    localeInfo
  }), [locale, setLocale, t, formatDate, formatDateTime, formatCurrency, formatNumber, localeInfo]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export { DEFAULT_LOCALE, STORAGE_KEY };
